import type { ReactElement } from 'react';
import React, { createRef, useState } from 'react';

import { trackAccordionExpanded } from './tracking';
import * as styles from './Accordion.styles';

export interface AccordionProps {
  bodyTestId?: string;
  children: ReactElement;
  heading: ReactElement;
}

export const Accordion = ({ bodyTestId, children, heading }: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const headingRef = createRef<HTMLElement>();

  const toggleExpanded = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      trackAccordionExpanded(headingRef.current?.textContent || '');
      setIsOpen(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const keyCode = e.key;

    if (['Enter', ' ', 'Spacebar'].includes(keyCode)) {
      e.preventDefault();
      toggleExpanded();
    }
  };

  const withExtraProps = (element: ReactElement) => {
    return React.cloneElement(element, { isOpen });
  };

  return (
    <details css={styles.accordionItem}>
      <summary
        aria-expanded={isOpen}
        css={styles.accordionHeading}
        onClick={toggleExpanded}
        onKeyDown={handleKeyPress}
        ref={headingRef}
      >
        {withExtraProps(heading)}
      </summary>
      <div
        data-testid={bodyTestId}
        role={isOpen ? 'region' : ''}
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        {children}
      </div>
    </details>
  );
};
